/**
 * Traffic (/dashboard/admin/traffic): Google Analytics 4 and Search Console for every site in the network, for Chicago
 * Politics staff (worker/src/traffic.js, worker/src/google-oauth.js; GET /api/dashboard/traffic). Not designed: built
 * from the Analysis and Alerts pages' parts (cards, stat tiles, members tables, chips) and the Overview's chart widget.
 *
 *   Connection  who connected Google and when, the GA4 property and each site's Search Console entry that discovery
 *               found (unverified domains listed), Connect / Reconnect / Disconnect, Refresh now
 *   Site        the whole network, or one site (GA4 splits sites by hostname; one property serves them all)
 *   GA4         KPI tiles with the change against the prior 28 days, the daily trend, top pages, landing pages,
 *               channels, sources, devices, places, the site's own events (and home_module_click by module, once
 *               registered)
 *   Search      clicks, impressions, CTR and position, the daily trend, top queries and pages, "striking distance"
 *               (position 5-20 with many impressions), devices and countries. Its days end a few days back: Search
 *               Console's data settles late.
 *   Ad fill     Adsterra slots filled, empty or unreported, by site and unit, today / 7 / 28 days (AdFill.tsx; our own
 *               counters, not Google's: shown whether or not Google is connected)
 *   Stripe      Connect status, the payout webhook button, and publisher payout batches to review and send
 *               (StripeConnect.tsx; worker/src/stripe-connect.js)
 * A part whose latest pull failed keeps its last good data; the page says which and since when. The page fetches its own
 * data, like Alerts; Connect is a full navigation to Google and back (?google=connected|denied|error).
 */
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { ExternalLink, RefreshCw } from "lucide-react";
import { List, ListRow, RowSub, RowTitle } from "../components/shared/List";
import { ChartWidget } from "../overview/charts/ChartWidget";
import { toastStore } from "../stores/toastStore";
import { errorText } from "../lib/apiError";
import { Cell, Head } from "./Organizations";
import { AdFillCard } from "./AdFill";   // Ad fill: Adsterra slots filled, empty or unreported (worker/src/ad-fill.js)
import { PayoutBatches, StripeConnectCard } from "./StripeConnect";   // Stripe Connect: publisher payouts (worker/src/stripe-connect.js)
import {
  NETWORK, count, delta, duration, events, format, gaErrors, gscView, kpis, moduleClicks, num, pageUrl, pct, rowsFor, siteName, trend,
  type Delta, type GaRow, type GscRow, type TrafficReport, type TrafficSnapshot,
} from "./trafficData";

const API = "/api/dashboard/traffic";
const when = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "");
const day = (iso: string | null | undefined) => (iso ? new Date(`${iso.slice(0, 10)}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" }) : "");
const OUTCOME: Record<string, string> = {
  connected: "Google connected. Refresh to pull the numbers now, or wait for tomorrow morning's pull.",
  denied: "Google wasn't connected: the request was declined.",
  error: "Google wasn't connected. Try again.",
};
const TREND = [{ key: "activeUsers", label: "Users" }, { key: "sessions", label: "Sessions" }, { key: "screenPageViews", label: "Views" }];

async function post(path: string): Promise<{ ok: boolean; body: (TrafficReport & { error?: unknown }) | null }> {
  const res = await fetch(path, { method: "POST", credentials: "include" }).catch(() => null);
  const body = res ? await res.json().catch(() => null) : null;
  if (!res?.ok) toastStore.getState().show({ message: errorText(body) ?? "Couldn't do that. Try again." });
  return { ok: !!res?.ok, body };
}

function DeltaChip({ d }: { d: Delta | null }) {
  if (!d) return null;
  return <span className={`chart-headline__delta ${d.good == null ? "is-flat" : d.good ? "is-up" : "is-down"}`}>{d.dir === "up" ? "↑ " : d.dir === "down" ? "↓ " : ""}{d.text}</span>;
}

function Section({ id, title, sub, children }: { id: string; title: string; sub?: ReactNode; children: ReactNode }) {
  return (
    <section className="approvals-recent" aria-labelledby={id}>
      <h2 className="section-title" id={id}>{title}</h2>
      {sub && <p className="muted art-small">{sub}</p>}
      {children}
    </section>
  );
}

/** A table of rows: the first column's text (and link), then numbers. */
function Table<R>({ label, rows, first, cols, empty = "No data yet" }: {
  label: string; rows: R[]; empty?: string;
  first: { head: string; cell: (r: R) => ReactNode };
  cols: { head: string; width?: number; cell: (r: R) => ReactNode }[];
}) {
  if (!rows.length) return <p className="empty-panel">{empty}</p>;
  return (
    <List as="div" className="members admin-analysis-errors" role="table" aria-label={label}>
      <ListRow as="div" className="members__head ls-head" role="row"><Head min={220}>{first.head}</Head>{cols.map((c) => <Head key={c.head} width={c.width ?? 100}>{c.head}</Head>)}</ListRow>
      {rows.map((r, i) => (
        <ListRow key={i} as="div" className="members__row" role="row">
          <Cell min={220} className="approvals__org">{first.cell(r)}</Cell>
          {cols.map((c) => <Cell key={c.head} width={c.width ?? 100} className="members__active">{c.cell(r)}</Cell>)}
        </ListRow>
      ))}
    </List>
  );
}

function Connection({ r, busy, onDisconnect }: { r: TrafficReport; busy: boolean; onDisconnect: () => void }) {
  const c = r.connection, sites = r.snapshot?.sites ?? [];
  const chip = !c.configured ? ["chip--neutral", "Not set up"] : c.connected ? ["chip--positive", "Connected"] : c.status === "revoked" ? ["chip--warning", "Reconnect needed"] : ["chip--neutral", "Not connected"];
  return (
    <section className="access-card" aria-labelledby="traffic-google">
      <h2 id="traffic-google">Google <span className={`chip ${chip[0]}`}>{chip[1]}</span></h2>
      {!c.configured ? <p className="muted">The Worker needs the secrets GOOGLE_CLIENT_ID and GOOGLE_CLIENT_SECRET (docs/MULTI_CITY.md, "Traffic").</p> : <>
        {c.status ? (
          <p className="muted">{c.email ?? "A Google account"} · connected {when(c.connectedAt)}{c.property ? ` · GA4 property ${c.property.name ? `${c.property.name} (${c.property.id})` : c.property.id}` : ""}</p>
        ) : <p className="muted">Connect the Google account that can read the network's GA4 property and its sites in Search Console. Read-only.</p>}
        {c.missingScopes.length > 0 && <p className="art-small art-warn">Not granted: {c.missingScopes.map((s) => s.replace("https://www.googleapis.com/auth/", "")).join(", ")}. Reconnect and allow both.</p>}
        {c.lastError && <p className="art-small art-warn">{c.lastError}</p>}
        {c.discoveryError && <p className="art-small art-warn">{c.discoveryError}</p>}
        {c.status && Object.keys(c.searchConsole.sites).length > 0 && (
          // Each site's Search Console entry as discovery found it (the domain once a pull has listed the sites).
          <div>{Object.entries(c.searchConsole.sites).map(([slug, entry]) => (
            <div key={slug} className="access-rule"><span>{sites.find((s) => s.slug === slug)?.domain ?? slug}</span><b>{entry ?? "Not verified in Search Console"}</b></div>
          ))}</div>
        )}
        <span className="art-row-actions">
          <a className="btn btn--primary btn--small" href="/api/dashboard/google/connect">{c.status ? "Reconnect" : "Connect Google"}</a>
          {c.status && <button className="btn btn--secondary btn--small" disabled={busy} onClick={onDisconnect}>Disconnect</button>}
        </span>
      </>}
    </section>
  );
}

/** GA4 for one view (exported for tests/traffic.test.mts, which renders it from a pulled snapshot). */
export function Ga({ s, site }: { s: TrafficSnapshot; site: string }) {
  const [metric, setMetric] = useState("activeUsers");
  const tiles = kpis(s, site);
  const t = trend(s, site, metric);
  const pages = rowsFor(s, "ga.pages", site).slice(0, 50);
  const landing = rowsFor(s, "ga.landing", site).slice(0, 25);
  const channels = rowsFor(s, "ga.channels", site);
  const sources = rowsFor(s, "ga.sources", site).slice(0, 20);
  const devices = rowsFor(s, "ga.devices", site);
  const geo = rowsFor(s, "ga.geo", site).slice(0, 50);
  const ev = events(s, site);
  const modules = moduleClicks(s, site);
  const many = site === NETWORK;
  const where = (r: GaRow, path: string) => <><RowTitle as="b"><a href={pageUrl(s, String(r.site), path)} target="_blank" rel="noopener">{path || "(not set)"}</a></RowTitle>{many && <RowSub>{siteName(s, String(r.site))}</RowSub>}</>;
  return <>
    {tiles ? (
      <div className="stat-row traffic-kpis">
        {tiles.map((k) => <div key={k.key} className="stat"><span>{k.label}</span><b>{k.value}</b><DeltaChip d={k.delta} /></div>)}
      </div>
    ) : <p className="empty-panel">No GA4 totals yet.</p>}

    <Section id="traffic-trend" title="Every day" sub={t.from ? `${day(t.from)} – ${day(t.to)} · ${count(t.total)} ${TREND.find((x) => x.key === metric)!.label.toLowerCase()}` : undefined}>
      <div className="seg" role="radiogroup" aria-label="Trend measure">
        {TREND.map((x) => <button key={x.key} role="radio" aria-checked={metric === x.key} onClick={() => setMetric(x.key)}>{x.label}</button>)}
      </div>
      {t.points.length ? <div className="traffic-chart"><ChartWidget type="line" shape={{ kind: "time", unit: "count", points: t.points }} w={12} h={4} title={TREND.find((x) => x.key === metric)!.label} source={{ kind: "real", text: "Google Analytics 4" }} /></div>
        : <p className="empty-panel">No trend yet.</p>}
    </Section>

    <Section id="traffic-pages" title="Top pages" sub="Views in the last 28 days">
      <Table label="Top pages" rows={pages} first={{ head: "Page", cell: (r) => where(r, String(r.pagePath)) }} cols={[
        { head: "Views", cell: (r) => count(num(r, "screenPageViews")) }, { head: "Users", cell: (r) => count(num(r, "activeUsers")) },
        { head: "Engaged", cell: (r) => pct(num(r, "engagementRate")) }, { head: "Time / user", cell: (r) => duration(num(r, "engagementPerUser")) },
        { head: "Key events", cell: (r) => count(num(r, "keyEvents")) }]} />
    </Section>

    <Section id="traffic-landing" title="Landing pages" sub="Where sessions start">
      <Table label="Landing pages" rows={landing} first={{ head: "Landing page", cell: (r) => where(r, String(r.landingPage)) }} cols={[
        { head: "Sessions", cell: (r) => count(num(r, "sessions")) }, { head: "Engaged", cell: (r) => pct(num(r, "engagementRate")) },
        { head: "Duration", cell: (r) => duration(num(r, "averageSessionDuration")) }, { head: "Key events", cell: (r) => count(num(r, "keyEvents")) }]} />
    </Section>

    <div className="traffic-pair">
      <Section id="traffic-channels" title="Channels" sub="Sessions by default channel group">
        {channels.length ? <div className="traffic-chart"><ChartWidget type="bar" w={6} h={4} title="Channels" source={{ kind: "real", text: "Google Analytics 4" }}
          shape={{ kind: "category", unit: "count", rows: channels.map((r) => ({ key: String(r.sessionDefaultChannelGroup), label: String(r.sessionDefaultChannelGroup), value: num(r, "sessions") })) }} /></div>
          : <p className="empty-panel">No data yet</p>}
      </Section>
      <Section id="traffic-devices" title="Devices" sub="Users by device">
        {devices.length ? <div className="traffic-chart"><ChartWidget type="donut" w={6} h={4} title="Devices" source={{ kind: "real", text: "Google Analytics 4" }}
          shape={{ kind: "parts", unit: "count", parts: devices.map((r) => ({ key: String(r.deviceCategory), label: String(r.deviceCategory), value: num(r, "activeUsers") })) }} /></div>
          : <p className="empty-panel">No data yet</p>}
      </Section>
    </div>

    <Section id="traffic-sources" title="Sources">
      <Table label="Sources" rows={sources} first={{ head: "Source / medium", cell: (r) => <RowTitle as="b">{String(r.sessionSourceMedium)}</RowTitle> }} cols={[
        { head: "Sessions", cell: (r) => count(num(r, "sessions")) }, { head: "Users", cell: (r) => count(num(r, "activeUsers")) },
        { head: "Engaged", cell: (r) => pct(num(r, "engagementRate")) }, { head: "Key events", cell: (r) => count(num(r, "keyEvents")) }]} />
    </Section>

    <Section id="traffic-events" title="Events" sub="The site's own events first, then GA4's busiest">
      <Table label="Events" rows={[...ev.custom, ...ev.other]} first={{ head: "Event", cell: (r) => <RowTitle as="b">{r.name}</RowTitle> }} cols={[
        { head: "Count", cell: (r) => count(r.count) }, { head: "Users", cell: (r) => count(r.users) }]} />
      {modules ? (
        <Table label="home_module_click by module" rows={modules} first={{ head: "home_module_click: module", cell: (r) => <RowTitle as="b">{r.module}</RowTitle> }} cols={[{ head: "Clicks", cell: (r) => count(r.count) }]} />
      ) : <p className="muted art-small">home_module_click by module shows once "module" is registered as an event-scoped custom dimension in GA4 (Admin, Custom definitions).</p>}
    </Section>

    <Section id="traffic-geo" title="Places" sub="Users by city, top 50">
      <Table label="Places" rows={geo} first={{ head: "City", cell: (r) => <><RowTitle as="b">{String(r.city)}</RowTitle><RowSub>{String(r.region)}{many && r.site !== NETWORK ? ` · ${siteName(s, String(r.site))}` : ""}</RowSub></> }} cols={[
        { head: "Users", cell: (r) => count(num(r, "activeUsers")) }, { head: "Sessions", cell: (r) => count(num(r, "sessions")) }]} />
    </Section>
  </>;
}

/** Search Console for one view (exported for the tests too). */
export function Search({ s, site }: { s: TrafficSnapshot; site: string }) {
  const v = gscView(s, site);
  const r = s.ranges?.gsc;
  const many = v.sites.length > 1;
  const gscCols = [
    { head: "Clicks", width: 80, cell: (x: GscRow) => count(x.clicks) }, { head: "Impressions", width: 110, cell: (x: GscRow) => count(x.impressions) },
    { head: "CTR", width: 70, cell: (x: GscRow) => pct(x.ctr) }, { head: "Position", width: 80, cell: (x: GscRow) => x.position.toFixed(1) },
  ];
  const label = (x: GscRow, text: string, href?: string) => <><RowTitle as="b">{href ? <a href={href} target="_blank" rel="noopener">{text}</a> : text}</RowTitle>{many && <RowSub>{siteName(s, x.site ?? "")}</RowSub>}</>;
  const tiles: { label: string; kind: "count" | "rate" | "position"; key: keyof GscRow; lower?: boolean }[] = [
    { label: "Clicks", kind: "count", key: "clicks" }, { label: "Impressions", kind: "count", key: "impressions" },
    { label: "CTR", kind: "rate", key: "ctr" }, { label: "Average position", kind: "position", key: "position", lower: true },
  ];
  return (
    <Section id="traffic-search" title="Search Console" sub={r ? `${day(r.current.startDate)} – ${day(r.current.endDate)}: Search Console's numbers settle about ${r.lagDays} days late.` : undefined}>
      {v.unverified.length > 0 && <p className="art-small art-warn">Not verified in Search Console: {v.unverified.map((x) => x.domain).join(", ")}. Verify the domain property (sc-domain:) with this Google account, and the next pull finds it.</p>}
      {v.errors.length > 0 && <p className="art-small art-warn">The latest pull failed for {v.errors.map((e) => e.part.replace(/^gsc\./, "")).join(", ")}; showing the last good data. {v.errors[0].error}</p>}
      {!v.sites.length ? null : <>
        {v.totals && <div className="stat-row traffic-kpis">
          {tiles.map((k) => <div key={k.label} className="stat"><span>{k.label}</span><b>{format(Number(v.totals!.current[k.key as "clicks"]), k.kind)}</b>
            <DeltaChip d={delta(Number(v.totals!.current[k.key as "clicks"]), Number(v.totals!.prior[k.key as "clicks"]), k.kind, k.lower)} /></div>)}
        </div>}
        {v.trend.length > 0 && <div className="traffic-chart"><ChartWidget type="line" shape={{ kind: "time", unit: "count", points: v.trend }} w={12} h={4} title="Clicks from Google Search" source={{ kind: "real", text: "Search Console" }} /></div>}
        <h3 className="art-card__title">Top queries</h3>
        <Table label="Top queries" rows={v.queries} first={{ head: "Query", cell: (x) => label(x, x.query ?? "") }} cols={gscCols} />
        <h3 className="art-card__title">Top pages</h3>
        <Table label="Top pages in search" rows={v.pages} first={{ head: "Page", cell: (x) => label(x, (x.page ?? "").replace(/^https?:\/\/[^/]+/, "") || "/", x.page) }} cols={gscCols} />
        <h3 className="art-card__title">Striking distance</h3>
        <p className="muted art-small">Queries where a page ranks 5th to 20th and is seen often: the closest wins. Most impressions first.</p>
        <Table label="Striking distance" rows={v.striking} first={{ head: "Query → page", cell: (x) => <><RowTitle as="b">{x.query}</RowTitle><RowSub><a href={x.page} target="_blank" rel="noopener">{(x.page ?? "").replace(/^https?:\/\//, "")}</a></RowSub></> }} cols={gscCols} />
        <div className="traffic-pair">
          <div><h3 className="art-card__title">Devices</h3><Table label="Search devices" rows={v.devices} first={{ head: "Device", cell: (x) => <RowTitle as="b">{x.device}</RowTitle> }} cols={gscCols.slice(0, 2)} /></div>
          <div><h3 className="art-card__title">Countries</h3><Table label="Search countries" rows={v.countries} first={{ head: "Country", cell: (x) => <RowTitle as="b">{String(x.country).toUpperCase()}</RowTitle> }} cols={gscCols.slice(0, 2)} /></div>
        </div>
      </>}
    </Section>
  );
}

export function AdminTraffic() {
  const [r, setR] = useState<TrafficReport | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState<"refresh" | "disconnect" | null>(null);
  const [site, setSite] = useState<string>(NETWORK);
  useEffect(() => { document.title = "Traffic · Chicago Politics"; }, []);
  const load = () => fetch(API, { credentials: "include" }).then((x) => (x.ok ? x.json() : Promise.reject())).then(setR).catch(() => setFailed(true));
  useEffect(() => {
    load();
    // Back from Google: say how it went, then drop ?google= from the address.
    const q = new URLSearchParams(location.search), outcome = q.get("google");
    if (outcome) {
      toastStore.getState().show({ message: `${OUTCOME[outcome] ?? OUTCOME.error}${outcome === "error" && q.get("reason") ? ` (${q.get("reason")})` : ""}` });
      history.replaceState(null, "", location.pathname);
    }
  }, []);
  const refresh = async () => {
    setBusy("refresh");
    const { body } = await post(`${API}/refresh`);
    setBusy(null);
    if (body?.connection) setR({ connection: body.connection, snapshot: body.snapshot });
  };
  // Connected but never pulled (just back from Google): pull once.
  useEffect(() => { if (r?.connection.connected && !r.snapshot?.generatedAt && !busy) refresh(); }, [r?.connection.connected]); // eslint-disable-line react-hooks/exhaustive-deps
  const disconnect = async () => {
    if (!confirm("Disconnect Google? The daily pull stops until someone connects again. The last numbers stay.")) return;
    setBusy("disconnect");
    const { ok } = await post("/api/dashboard/google/disconnect");
    setBusy(null);
    if (ok) load();
  };
  const s = r?.snapshot ?? null;
  const sites = useMemo(() => [{ slug: NETWORK, name: "Network" }, ...(s?.sites ?? [])], [s]);
  const errs = gaErrors(s);
  return (
    <div className="access-page admin-page traffic-page">
      <header className="access-head">
        <h1 className="page-title">Traffic</h1>
        {r?.connection.connected && <span className="art-row-actions">
          <a className="btn btn--secondary btn--small" href="https://analytics.google.com/" target="_blank" rel="noopener"><ExternalLink size={14} aria-hidden />GA4</a>
          <button className="btn btn--primary btn--small" disabled={!!busy} onClick={refresh}><RefreshCw size={14} aria-hidden />{busy === "refresh" ? "Pulling…" : "Refresh now"}</button>
        </span>}
      </header>
      {failed ? <p className="empty-panel">Couldn't load traffic. Try again.</p> : !r ? <p className="empty-panel">Loading…</p> : <>
        <div className="access-cards admin-analysis traffic-cards">
          <Connection r={r} busy={!!busy} onDisconnect={disconnect} />
          {s?.lastRun && (
            <section className="access-card" aria-labelledby="traffic-run">
              <h2 id="traffic-run">Last pull</h2>
              <p className="muted">{when(s.lastRun.at)} · {s.lastRun.by === "cron" ? "daily" : s.lastRun.by} · {s.lastRun.ok} reports, {s.lastRun.failed} failed</p>
              {s.lastRun.error && <p className="art-small art-warn">{s.lastRun.error}</p>}
              {errs.length > 0 && <p className="art-small art-warn">Showing older data for {errs.map((e) => `${e.part.replace(/^ga\./, "")}${e.at ? ` (from ${when(e.at)})` : ""}`).join(", ")}: {errs[0].error}</p>}
              {s.quota?.ga?.tokensPerDay?.remaining != null && <p className="muted art-small">GA4 quota left today: {count(s.quota.ga.tokensPerDay.remaining)} tokens</p>}
            </section>
          )}
        </div>
        {s?.generatedAt ? <>
          <div className="seg traffic-sites" role="radiogroup" aria-label="Site">
            {sites.map((x) => <button key={x.slug} role="radio" aria-checked={site === x.slug} onClick={() => setSite(x.slug)}>{x.name}</button>)}
          </div>
          <p className="muted art-small">The last 28 days through yesterday, against the 28 before. {site === NETWORK ? "Users and sessions are counted once across the network." : `${siteName(s, site)} only.`}</p>
          <Ga s={s} site={site} />
          <Search s={s} site={site} />
        </> : r.connection.connected ? <p className="empty-panel">{busy === "refresh" ? "Pulling the first numbers…" : "No numbers yet."}</p> : null}
      </>}
      <AdFillCard />
      <div className="access-cards admin-analysis traffic-cards"><StripeConnectCard /></div>
      <PayoutBatches />

    </div>
  );
}
